import { Asset } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { pluralize } from '@/utils/format';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, ListItemIcon } from '@mui/material';
import { useState } from 'react';
import { StyledList, StyledListItem } from '../AssetsPage/PopoverList';
import { RoundedPopover } from '../Popover/RoundedPopover';

export const SuitableSubstitutesFooter = ({
  matches,
  hiddenAssetIds,
  allAssets,
  selectedAsset,
  onUnhideAsset,
  onUnhideAll,
}: {
  matches: Asset[];
  hiddenAssetIds: Set<string>;
  allAssets: Asset[];
  selectedAsset?: Asset;
  onUnhideAsset: (assetId: string) => void;
  onUnhideAll: () => void;
}) => {
  const [hiddenAnchorElement, setHiddenAnchorElement] = useState<HTMLButtonElement>();
  const hiddenPopoverOpen = Boolean(hiddenAnchorElement);

  const handleHiddenClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHiddenAnchorElement(event.currentTarget);
  };

  const handleHiddenClose = () => {
    setHiddenAnchorElement(undefined);
  };

  return (
    <Box height='40px' display='flex' justifyContent='flex-start' alignItems='stretch'>
      <Box sx={{ padding: '8px' }}>
        <BodyText sx={{ color: 'text.primary' }}>{pluralize(matches.length, 'option', 'options')}</BodyText>
      </Box>
      {hiddenAssetIds.size > 0 && (
        <Button variant='text' size='small' onClick={handleHiddenClick}>
          <VisibilityOff sx={{ fontSize: '18px', margin: '0 6px 0 4px', color: 'text.secondary' }} />
          <BodyText>{hiddenAssetIds.size} hidden</BodyText>
        </Button>
      )}
      <RoundedPopover
        open={hiddenPopoverOpen}
        anchorEl={hiddenAnchorElement}
        onClose={handleHiddenClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ minWidth: '200px', maxWidth: '300px' }}>
          <Box
            sx={{
              padding: '8px 16px',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <BodyText>Hidden Options ({hiddenAssetIds.size})</BodyText>
            <Button size='small' onClick={onUnhideAll}>
              Unhide All
            </Button>
          </Box>
          <StyledList>
            {[...hiddenAssetIds].map(assetId => {
              const asset = allAssets.find(a => a.id === assetId);
              if (!asset) return;
              const assetName = asset.path?.split('/').pop() || 'Unknown Asset';
              const originalIndex = allAssets.findIndex(a => a.id === asset.id);
              const optionNumber = selectedAsset ? originalIndex : originalIndex + 1;

              return (
                <StyledListItem
                  key={assetId}
                  onClick={() => onUnhideAsset(assetId)}
                  sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}
                >
                  <ListItemIcon>
                    <Visibility sx={{ fontSize: '18px' }} />
                  </ListItemIcon>
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <BodyText sx={{ fontSize: '12px', fontWeight: 500 }}>Option {optionNumber}</BodyText>
                    <BodyText
                      sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {assetName}
                    </BodyText>
                  </Box>
                </StyledListItem>
              );
            })}
          </StyledList>
        </Box>
      </RoundedPopover>
    </Box>
  );
};
